import { useLayoutEffect, useMemo, useCallback, useEffect, useRef, useState } from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import type { FlatRow } from "../types";
import { clampPx, computeZoomConfig, DEFAULT_PX, MIN_PX, MAX_PX } from "../constants";
import { sanitizeWorkCode } from "../utils";
import {
  applyFilters,
  buildAttributes,
  countActiveFilters,
  countEntries,
  encodeCompare,
  type ColorByMode,
  type CompareOp,
  type FilterState,
  type MatchMode,
  type TaktAttribute,
} from "../filtering/attributes";
import {
  colorChangedEvent,
  compareFilterChangedEvent,
  filterClearedEvent,
  filterValueChangedEvent,
  matchModeChangedEvent,
  type TrackedEvent,
} from "../filtering/tracking";
import { trackEvent } from "../../../api/clientV2";
import { useScheduleData } from "./useScheduleData";
import { useWorkdayGrid } from "./useWorkdayGrid";
import { useTaktLayout } from "./useTaktLayout";

function loadColorBy(): ColorByMode {
  const saved = localStorage.getItem("taktColorBy");
  if (saved === "workCode" || saved === "trade" || saved === "critical") {
    return saved;
  }
  // A stale "none" (or anything else) recovers to work-code coloring.
  if (localStorage.getItem("colorByTrade") === "true") return "trade";
  return "workCode";
}

/** Saved px-per-day, or null when the user hasn't picked a zoom yet (→ the
 *  6-week auto-default). One-time migrates the legacy discrete preset key. */
function loadPxPerDay(): number | null {
  const saved = localStorage.getItem("taktPxPerDay");
  if (saved != null) {
    const n = parseFloat(saved);
    if (Number.isFinite(n)) return clampPx(n);
  }
  const legacy = localStorage.getItem("taktZoomLevel");
  if (legacy === "overview" || legacy === "normal") {
    const px = legacy === "overview" ? 22 : 38;
    localStorage.setItem("taktPxPerDay", String(px));
    localStorage.removeItem("taktZoomLevel");
    return px;
  }
  return null;
}

/** Width of the sticky left zone-label column, subtracted from the viewport to
 *  get the day-grid area for the fit-to-width floor. Approximation: the real
 *  zone column is 60px at normal zoom but 40px at deep zoom-out (see
 *  computeZoomConfig.zoneColWidth), so on very long schedules zoomed all the way
 *  out a ~20px gap can open on the right. Tolerable for now. */
const LEFT_COL_PX = 60;

/** Default lookahead window when the user hasn't zoomed yet: ~6 work-weeks. */
const DEFAULT_WEEKS_WORKDAYS = 30;

/**
 * Top-level hook for TaktBoardPage. Composes sub-hooks for data fetching,
 * workday grid computation, and layout, then adds the color/filter model
 * (one attribute set drives both): filtering projects the board down to the
 * matching activities before layout, so only matching rows render.
 */
export function useTaktBoard() {
  const {
    schedules,
    selectedScheduleId,
    loading,
    error,
    data,
    reload,
    updateEntryDates,
    updateBulkEntryDates,
  } = useScheduleData();

  const workdays = data?.workdays ?? [];
  const { monthSpans, todayIdx, weekStartIdx } = useWorkdayGrid(workdays);

  // ----- Continuous zoom -----
  // userPx is the user's chosen slider value, or null until they touch the
  // slider (→ the 6-week auto-default). Only explicit choices persist.
  const [userPx, setUserPx] = useState<number | null>(loadPxPerDay);
  // Coalesce slider commits (and the localStorage write) to one per frame so a
  // fast drag doesn't queue a relayout + synchronous disk write per input event.
  const pxPendingRef = useRef<number | null>(null);
  const pxRafRef = useRef<number | null>(null);
  const setPxPerDay = useCallback((n: number) => {
    pxPendingRef.current = clampPx(n);
    if (pxRafRef.current == null) {
      pxRafRef.current = requestAnimationFrame(() => {
        pxRafRef.current = null;
        const pending = pxPendingRef.current;
        if (pending != null) {
          setUserPx(pending);
          localStorage.setItem("taktPxPerDay", String(pending));
        }
      });
    }
  }, []);

  // Measured width of the board's scroll container (kept in sync below). Drives
  // the fit-to-width floor and the 6-week default. Seeded from the window so the
  // first paint already has a real width (no flash from a 0/placeholder snap);
  // the ResizeObserver below refines it to the exact scroller width.
  const [viewportW, setViewportW] = useState(() =>
    typeof window !== "undefined" ? Math.max(0, window.innerWidth - 32) : 0,
  );
  const dayArea = Math.max(1, viewportW - LEFT_COL_PX);

  // Default lookahead when nothing is saved: ~6 work-weeks fill the width.
  // Rounded so the slider thumb (step=1) lands exactly on this value.
  const sixWeekPx =
    viewportW > 0 ? clampPx(Math.round(dayArea / DEFAULT_WEEKS_WORKDAYS)) : DEFAULT_PX;
  // The slider value actually in effect (raw — may sit BELOW fit-to-width).
  const basePx = userPx ?? sixWeekPx;

  // "Zoomed all the way out" must FILL the screen with the whole project, not
  // leave a dead gap to the right — so the horizontal axis (colWidth) is floored
  // at fit-to-width. The vertical lever (rowPx) is free to drop below that:
  // that's how "zoom out further" keeps shrinking rows after the time axis maxes.
  const fitPx =
    workdays.length > 0 && viewportW > 0
      ? Math.min(MAX_PX, Math.max(MIN_PX, dayArea / workdays.length))
      : MIN_PX;
  const colWidth = Math.max(basePx, fitPx);
  const rowPx = basePx;
  const zoomConfig = useMemo(
    () => computeZoomConfig(colWidth, rowPx),
    [colWidth, rowPx],
  );

  // Weeks the time axis currently shows (or the whole project) — the slider
  // readout. Below fit-to-width the horizontal axis is pinned, so this reads
  // "Full project" while the slider keeps compressing rows.
  const daysShown = dayArea / colWidth;
  const atFullProject = workdays.length > 0 && daysShown >= workdays.length - 1;
  const weeksShown = Math.max(1, Math.round(daysShown / 5));
  const zoomReadout =
    viewportW === 0
      ? ""
      : atFullProject
        ? "Full project"
        : `~${weeksShown} week${weeksShown === 1 ? "" : "s"}`;

  // Scroll-to-section lands the section header just below the sticky date
  // header, plus a small landing gap so it isn't flush against it.
  const HEADER_LANDING_GAP = 4;
  const stickyHeaderHeight = zoomConfig.headerHeight + HEADER_LANDING_GAP;

  // ----- View-action telemetry -----
  // The filter/color handlers below are declared before `attributes` and the
  // live filter/match state are computed, so read those through refs (refreshed
  // each render) to resolve human labels and the on/off transition at fire time.
  // emit() keeps the fire-and-forget trackEvent out of every handler body.
  const attributesRef = useRef<TaktAttribute[]>([]);
  const filtersRef = useRef<FilterState>({});
  const matchModeRef = useRef<MatchMode>("all");
  const emit = useCallback((ev: TrackedEvent) => {
    void trackEvent(ev.event, ev.properties);
  }, []);

  // ----- Color mode (persisted) -----
  const [colorBy, setColorByRaw] = useState<ColorByMode>(loadColorBy);
  const setColorBy = useCallback(
    (mode: ColorByMode) => {
      setColorByRaw(mode);
      localStorage.setItem("taktColorBy", mode);
      emit(colorChangedEvent(mode));
    },
    [emit],
  );

  // ----- Filters (session state — deliberately not persisted, so a stale
  //       narrowed board never greets the user on reload) -----
  const [filters, setFilters] = useState<FilterState>({});
  const [matchMode, setMatchModeRaw] = useState<MatchMode>("all");
  filtersRef.current = filters;
  matchModeRef.current = matchMode;

  const setMatchMode = useCallback(
    (mode: MatchMode) => {
      setMatchModeRaw(mode);
      emit(matchModeChangedEvent(mode));
    },
    [emit],
  );

  const setAttrFilter = useCallback((key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (values.length === 0) delete next[key];
      else next[key] = values;
      return next;
    });
  }, []);

  const toggleAttrValue = useCallback(
    (key: string, valueId: string) => {
      const wasSelected = (filtersRef.current[key] || []).includes(valueId);
      setFilters((prev) => {
        const cur = prev[key] || [];
        const nextVals = cur.includes(valueId)
          ? cur.filter((v) => v !== valueId)
          : [...cur, valueId];
        const next = { ...prev };
        if (nextVals.length === 0) delete next[key];
        else next[key] = nextVals;
        return next;
      });
      const attr = attributesRef.current.find((a) => a.key === key);
      emit(
        filterValueChangedEvent(
          attr,
          key,
          valueId,
          !wasSelected,
          matchModeRef.current,
        ),
      );
    },
    [emit],
  );

  const clearAttrFilter = useCallback(
    (key: string) => {
      const wasActive = key in filtersRef.current;
      setFilters((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (wasActive) {
        const attr = attributesRef.current.find((a) => a.key === key);
        emit(filterClearedEvent("attribute", attr, key));
      }
    },
    [emit],
  );

  const clearAllFilters = useCallback(() => {
    const hadActive = Object.keys(filtersRef.current).length > 0;
    setFilters({});
    if (hadActive) emit(filterClearedEvent("all"));
  }, [emit]);

  const setCompare = useCallback(
    (key: string, op: CompareOp, value: number | null) => {
      setAttrFilter(
        key,
        value == null || Number.isNaN(value) ? [] : [encodeCompare(op, value)],
      );
      const attr = attributesRef.current.find((a) => a.key === key);
      emit(compareFilterChangedEvent(attr, key, op, value, matchModeRef.current));
    },
    [setAttrFilter, emit],
  );

  // ----- Attributes + filtered board (the generic engine) -----
  const attributes = useMemo(() => (data ? buildAttributes(data) : []), [data]);
  attributesRef.current = attributes;
  const filteredData = useMemo(
    () => (data ? applyFilters(data, attributes, filters, matchMode) : null),
    [data, attributes, filters, matchMode],
  );
  // applyFilters returns the same `data` reference when nothing is active.
  const isFiltering = filteredData !== null && filteredData !== data;
  const activeFilterCount = countActiveFilters(filters);

  const totalActivities = useMemo(() => countEntries(data), [data]);
  const shownActivities = useMemo(() => countEntries(filteredData), [filteredData]);
  const totalSections = data ? data.sections.length : 0;
  const shownSections = filteredData ? filteredData.sections.length : 0;

  const scrollerRef = useRef<HTMLElement>(null);

  // Keep viewportW in sync with the scroll container — initial mount, window
  // resize, and the Filters rail docking/undocking all change its width, and
  // each should re-derive the fit-to-width floor.
  useEffect(() => {
    let ro: ResizeObserver | null = null;
    let raf = 0;
    const measure = () => {
      const el = scrollerRef.current;
      setViewportW(el ? el.clientWidth : window.innerWidth);
    };
    const attach = () => {
      const el = scrollerRef.current;
      if (el) {
        ro = new ResizeObserver(measure);
        ro.observe(el);
        measure();
      } else {
        raf = requestAnimationFrame(attach);
      }
    };
    attach();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Center-day-preserving zoom: capture the day under the viewport center BEFORE
  // the px commit (against live geometry), then re-pin it AFTER the new widths
  // paint, in a layout effect. A single scrollLeft write — the header is sticky
  // inside this one scroll container, so there's no second scroller to sync.
  // Day i lives at content-x = (legendColWidth + zoneColWidth) + i*colWidth (the
  // sticky legend + zone columns precede the day grid), so that origin offset
  // must be subtracted on capture and re-added on re-pin — identically, or the
  // fixed offset gets scaled by the zoom ratio and the board drifts sideways.
  // anchorX = the x (within the viewport) to keep fixed across the zoom. The
  // slider omits it → viewport center; ⌘/Ctrl-wheel passes the cursor x so the
  // board zooms into wherever you're pointing.
  const pendingCenterDayRef = useRef<number | null>(null);
  const pendingAnchorXRef = useRef<number>(0);
  const onZoom = useCallback(
    (next: number, anchorX?: number) => {
      const el = scrollerRef.current;
      if (el) {
        const origin = zoomConfig.legendColWidth + zoomConfig.zoneColWidth;
        const ax = anchorX ?? el.clientWidth / 2;
        pendingAnchorXRef.current = ax;
        pendingCenterDayRef.current = (el.scrollLeft + ax - origin) / colWidth;
      }
      setPxPerDay(next);
    },
    [colWidth, zoomConfig.legendColWidth, zoomConfig.zoneColWidth, setPxPerDay],
  );
  useLayoutEffect(() => {
    const cd = pendingCenterDayRef.current;
    const el = scrollerRef.current;
    if (cd == null || !el) return;
    const origin = zoomConfig.legendColWidth + zoomConfig.zoneColWidth;
    // Round to a whole pixel — a fractional scrollLeft lands bars on sub-pixel
    // boundaries and leaves a sliver poking past the sticky gutter.
    el.scrollLeft = Math.max(0, Math.round(cd * zoomConfig.colWidth + origin - pendingAnchorXRef.current));
    pendingCenterDayRef.current = null;
  }, [basePx, zoomConfig.colWidth, zoomConfig.legendColWidth, zoomConfig.zoneColWidth]);

  const { flatRows } = useTaktLayout(filteredData, zoomConfig, attributes);

  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // Track the topmost visible section so a filter change can keep it in view
  // instead of snapping to the top of the board.
  const flatRowsRef = useRef(flatRows);
  flatRowsRef.current = flatRows;
  const topSectionRef = useRef<string | null>(null);
  const handleVisibleRange = useCallback((startIndex: number) => {
    const rows = flatRowsRef.current;
    for (let i = Math.min(startIndex, rows.length - 1); i >= 0; i--) {
      const r = rows[i];
      if (r && r.type === "section-header") {
        topSectionRef.current = r.section.name;
        return;
      }
    }
  }, []);

  // Snapshot the viewed section the moment the filters change (during render,
  // before the re-rendered list fires its own range-change at the top and
  // clobbers topSectionRef), so the restore effect targets where the user was.
  const pendingAnchorRef = useRef<string | null>(null);
  const prevFilterKeyRef = useRef<{ f: FilterState; m: MatchMode }>({
    f: filters,
    m: matchMode,
  });
  if (
    prevFilterKeyRef.current.f !== filters ||
    prevFilterKeyRef.current.m !== matchMode
  ) {
    pendingAnchorRef.current = topSectionRef.current;
    prevFilterKeyRef.current = { f: filters, m: matchMode };
  }

  // ----- Color stylesheet (CSS classes instead of inline styles). -----
  const activeColors = useMemo(() => {
    if (!data) return {};
    if (colorBy === "none") return {};
    if (colorBy === "trade") return data.colors;
    // Work code and critical use the per-schedule Takt_Color (work-code) palette.
    return data.static_colors || data.colors;
  }, [data, colorBy]);

  const colorStyleSheet: string = useMemo(() => {
    const rules: string[] = [];
    for (const [code, entry] of Object.entries(activeColors)) {
      const cls = sanitizeWorkCode(code);
      rules.push(`.takt-wc-${cls}{background-color:${entry.fill};color:${entry.font}}`);
    }
    return rules.join("\n");
  }, [activeColors]);

  // ----- Scroll to start of current week -----
  useEffect(() => {
    if (weekStartIdx === null || !scrollerRef.current) return;
    // Position week start right after the sticky legend column (whole pixel).
    const scrollLeft = Math.round(weekStartIdx * zoomConfig.colWidth);
    requestAnimationFrame(() => {
      scrollerRef.current?.scrollTo({ left: Math.max(0, scrollLeft), behavior: "auto" });
    });
    // data + zoomConfig excluded: only the initial week-jump should set scroll.
    // Zoom owns horizontal scroll via the center-day layout effect above; if this
    // re-fired on every zoom tick it would snap the board back to week-start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartIdx]);

  // When filters change, keep the section the user was viewing in place rather
  // than letting the re-rendered list snap back to the top.
  useEffect(() => {
    const name = pendingAnchorRef.current;
    if (!name) return;
    requestAnimationFrame(() => {
      const rows = flatRowsRef.current;
      const idx = rows.findIndex(
        (r) => r.type === "section-header" && r.section.name === name,
      );
      if (idx >= 0) {
        virtuosoRef.current?.scrollToIndex({
          index: idx,
          align: "start",
          offset: -stickyHeaderHeight,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, matchMode]);

  // ----- Section jump -----
  const scrollToSection = useCallback(
    (sectionName: string) => {
      const idx = flatRows.findIndex(
        (r: FlatRow) => r.type === "section-header" && r.section.name === sectionName,
      );
      if (idx >= 0 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: idx,
          align: "start",
          behavior: "smooth",
          offset: -stickyHeaderHeight,
        });
      }
    },
    [flatRows, stickyHeaderHeight],
  );

  return {
    schedules,
    selectedScheduleId,
    loading,
    error,
    data,
    colorBy,
    setColorBy,
    filters,
    matchMode,
    setMatchMode,
    setAttrFilter,
    toggleAttrValue,
    setCompare,
    clearAttrFilter,
    clearAllFilters,
    attributes,
    isFiltering,
    activeFilterCount,
    totalActivities,
    shownActivities,
    totalSections,
    shownSections,
    activeColors,
    monthSpans,
    todayIdx,
    flatRows,
    colorStyleSheet,
    scrollerRef,
    virtuosoRef,
    scrollToSection,
    handleVisibleRange,
    reload,
    updateEntryDates,
    updateBulkEntryDates,
    pxPerDay: basePx,
    defaultPx: sixWeekPx,
    zoomReadout,
    onZoom,
    zoomConfig,
  };
}
